// components/PromoTimer.js
import React, { useState, useEffect } from 'react';
import { Clock, Sparkles, ArrowRight, X, Zap, Star, Gift } from 'lucide-react';

const PromoTimer = ({ user }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    // Mostrar el popup después de un pequeño delay para la animación de entrada
    if (user?.plan === 'PRUEBA') {
      setTimeout(() => setIsAnimating(true), 300);
    }
  }, [user]);

  const closePopup = () => {
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 400);
  };

  const handlePlanClick = (planType) => {
    setSelectedPlan(planType);
    setShowPlanDetails(true);
  };

  const handleWhatsAppContact = (planName, price) => {
    const mensaje = `¡Hola! 👋

Me interesa adquirir el ${planName} que vi en su promoción especial.

📋 Detalles de mi solicitud:
• Plan solicitado: ${planName}
• Precio promocional: S/ ${price} por mes
• Usuario actual: ${user?.email || 'No especificado'}

Me gustaría conocer:
✅ Proceso de activación
✅ Formas de pago disponibles
✅ Tiempo de activación
✅ Próximos pasos

¡Muchas gracias! 😊`;
    
    const whatsappUrl = `https://wa.me/51942128139?text=${encodeURIComponent(mensaje)}`;
    window.open(whatsappUrl, '_blank');
    closePopup();
  };

  const planDetails = {
    basico: {
      name: 'Plan Básico',
      price: '25',
      originalPrice: '50',
      features: [
        'Seguimiento a Contratos Menores a 8 UIT',
        'Sistema de alerta de oportunidades',
        'Actualizaciones diarias',
        'Descuentos exclusivos en cursos y diplomados',
        'Soporte Técnico'
      ],
      description: 'Perfecto para emprendedores que quieren empezar en el mundo de las contrataciones públicas',
      icon: '🌱'
    },
    plus: {
      name: 'Plan Plus',
      price: '45',
      originalPrice: '180',
      features: [
        'Todo del Plan Básico',
        'Seguimiento del PAC y SEACE',
        'Catálogo de Proveedores',
        'Descuentos exclusivos en cursos y diplomados',
        'Soporte técnico prioritario'
      ],
      description: 'Ideal para empresas que buscan maximizar oportunidades de negocio en el sector público',
      icon: '👑'
    }
  };

  if (!user || user.plan !== 'PRUEBA' || !isVisible) return null;

  return (
    <>
      {/* Overlay con blur */}
      <div 
        className={`position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center transition-all ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(10px)',
          zIndex: 9999,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onClick={closePopup}
      >
        {/* Modal Container - Tamaño reducido */}
        <div 
          className={`position-relative mx-3 transition-all ${
            isAnimating ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
          }`}
          style={{
            maxWidth: '580px',
            width: '95%',
            maxHeight: '90vh',
            overflowY: 'auto',
            transform: isAnimating ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)',
            transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Popup Principal */}
          <div className="position-relative overflow-hidden" style={{
            background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 25%, #0f3460 50%, #533483 75%, #7209b7 100%)',
            borderRadius: '24px',
            boxShadow: '0 25px 50px rgba(26, 26, 46, 0.6), 0 0 0 2px rgba(255,215,0,0.2)',
            border: '2px solid rgba(255,215,0,0.3)'
          }}>
            
            {/* Efectos de fondo animados */}
            <div className="position-absolute w-100 h-100" style={{
              background: `
                radial-gradient(circle at 25% 25%, rgba(255,215,0,0.15) 0%, transparent 50%),
                radial-gradient(circle at 75% 75%, rgba(138,43,226,0.2) 0%, transparent 50%),
                linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.05) 50%, transparent 70%)
              `,
              animation: 'backgroundShift 8s ease-in-out infinite'
            }}></div>

            {/* Partículas flotantes reducidas */}
            {[...Array(6)].map((_, i) => (
              <div 
                key={i}
                className="position-absolute" 
                style={{
                  top: `${20 + (i * 12)}%`,
                  left: `${10 + (i * 13)}%`,
                  animation: `float ${4 + i}s ease-in-out infinite ${i * 0.5}s`,
                  opacity: 0.6 - (i * 0.08)
                }}
              >
                {i % 3 === 0 ? (
                  <Sparkles className="text-warning" size={8 + i} />
                ) : i % 3 === 1 ? (
                  <Star className="text-warning" size={7 + i} />
                ) : (
                  <Gift className="text-warning" size={6 + i} />
                )}
              </div>
            ))}

            {/* Botón cerrar */}
            <button
              onClick={closePopup}
              className="position-absolute top-0 end-0 m-3 d-flex align-items-center justify-content-center"
              style={{
                width: '40px',
                height: '40px',
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(255,215,0,0.5)',
                borderRadius: '50%',
                color: '#ffd700',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.8)';
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(0,0,0,0.6)';
                e.target.style.transform = 'scale(1)';
              }}
            >
              <X size={20} />
            </button>

            {/* Contenido principal - Más compacto */}
            <div className="position-relative px-4 py-4">
              
              {/* Header compacto */}
              <div className="text-center mb-3">
                <div className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-2" style={{
                  background: 'rgba(0,0,0,0.4)',
                  backdropFilter: 'blur(15px)',
                  border: '2px solid rgba(255,215,0,0.6)'
                }}>
                  <Zap className="text-warning" size={14} />
                  <span className="fw-bold" style={{ 
                    fontSize: '1rem',
                    color: '#ffd700',
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                  }}>OFERTA EXCLUSIVA</span>
                  <Sparkles className="text-warning" size={12} />
                </div>
                
                <h3 className="fw-bold mb-2" style={{
                  color: '#ffffff',
                  textShadow: '0 3px 10px rgba(0,0,0,0.8)',
                  fontSize: '1.5rem'
                }}>
                  ¡Empieza a ganar licitaciones!
                </h3>
                
                <p className="mb-0" style={{ 
                  color: '#e8e8e8', 
                  fontSize: '1rem',
                  textShadow: '0 2px 6px rgba(0,0,0,0.7)' 
                }}>
                  Descuento por tiempo limitado
                </p>
              </div>

              {/* Fecha límite más compacta */}
              <div className="text-center mb-4">
                <div className="d-inline-flex align-items-center flex-column gap-2 px-5 py-3 rounded-4 position-relative" style={{
                  background: 'rgba(0,0,0,0.6)',
                  backdropFilter: 'blur(25px)',
                  border: '3px solid rgba(255,215,0,0.7)',
                  boxShadow: '0 15px 40px rgba(0,0,0,0.7), 0 0 30px rgba(255,215,0,0.2)',
                  minWidth: '320px'
                }}>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <Clock className="text-warning" size={24} />
                    <div className="fw-bold text-center" style={{ 
                      color: '#ffffff',
                      fontSize: '1.1rem',
                      textShadow: '0 3px 8px rgba(0,0,0,0.8)',
                      letterSpacing: '0.5px',
                      lineHeight: '1.2'
                    }}>
                      PROMOCIÓN VÁLIDA<br/>HASTA EL
                    </div>
                    <Clock className="text-warning" size={24} />
                  </div>
                  
                  {/* Tarjeta de fecha más compacta */}
                  <div className="position-relative mb-2">
                    <div className="bg-gradient text-dark rounded-4 px-6 py-4 fw-bold shadow-lg position-relative d-inline-block" style={{
                      fontFamily: 'system-ui',
                      background: 'linear-gradient(145deg, #ffd700, #ffb347)',
                      boxShadow: '0 15px 40px rgba(255,215,0,0.7), inset 0 3px 0 rgba(255,255,255,0.8)',
                      border: '3px solid rgba(255,165,0,0.8)',
                      color: '#1a1a1a',
                      letterSpacing: '1.5px',
                      minWidth: '240px'
                    }}>
                      <div className="d-flex flex-column align-items-center">
                        <div style={{ 
                          fontSize: '2.8rem',
                          fontWeight: '900', 
                          lineHeight: '0.9',
                          textShadow: '0 3px 6px rgba(0,0,0,0.4)',
                          color: '#ffffff'
                        }}>17</div>
                        <div style={{ 
                          fontSize: '1rem',
                          fontWeight: '800', 
                          marginTop: '2px',
                          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                          color: '#ffffff'
                        }}>SETIEMBRE</div>
                        <div style={{ 
                          fontSize: '0.8rem', 
                          fontWeight: '600', 
                          marginTop: '1px',
                          opacity: 0.9,
                          color: '#ffffff'
                        }}>2025</div>
                      </div>
                      <div className="position-absolute top-0 start-0 w-100 h-100 rounded-4" style={{
                        background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.5) 50%, transparent 70%)',
                        animation: 'shimmer 3s infinite'
                      }}></div>
                    </div>
                  </div>
                  
                  {/* Indicador de urgencia más compacto */}
                  <div className="mt-1">
                    <div className="d-flex align-items-center justify-content-center gap-2 px-4 py-2 rounded-pill" style={{
                      background: 'rgba(220,53,69,0.8)',
                      border: '2px solid rgba(255,107,107,0.8)',
                      boxShadow: '0 10px 25px rgba(220,53,69,0.6)',
                      minWidth: '280px'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        animation: 'blink 1.2s ease-in-out infinite',
                        boxShadow: '0 0 15px rgba(255,255,255,0.9)'
                      }}></div>
                      <span className="fw-bold text-center" style={{ 
                        color: '#ffffff',
                        fontSize: '0.95rem',
                        textShadow: '0 3px 8px rgba(0,0,0,0.8)',
                        letterSpacing: '0.3px',
                        lineHeight: '1.2'
                      }}>
                        ¡SOLO QUEDAN 2 DÍAS<br/>
                      </span>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#ffffff',
                        animation: 'blink 1.2s ease-in-out infinite 0.6s',
                        boxShadow: '0 0 15px rgba(255,255,255,0.9)'
                      }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botones de precio más compactos */}
              <div className="row g-3 justify-content-center mb-3">
                <div className="col-md-6 col-12">
                  <div className="position-relative">
                    {/* Etiqueta de descuento */}
                    <div className="position-absolute top-0 end-0 translate-middle-y me-2 z-3">
                      <div className="d-flex align-items-center gap-1 px-2 py-1 fw-bold" style={{
                        background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                        fontSize: '0.75rem',
                        borderRadius: '15px',
                        boxShadow: '0 6px 20px rgba(25,135,84,0.6)',
                        color: 'white',
                        textShadow: '0 2px 4px rgba(0,0,0,0.7)'
                      }}>
                        50% OFF
                      </div>
                    </div>

                    <button 
                      className="btn w-100 py-3 fw-bold position-relative overflow-hidden"
                      onClick={() => handlePlanClick('basico')}
                      style={{
                        background: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(20px)',
                        border: '3px solid rgba(255,215,0,0.6)',
                        color: 'white',
                        borderRadius: '16px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.4), 0 0 20px rgba(255,215,0,0.2)',
                        minHeight: '140px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(0,0,0,0.6)';
                        e.target.style.transform = 'translateY(-2px) scale(1.01)';
                        e.target.style.boxShadow = '0 15px 35px rgba(0,0,0,0.5), 0 0 30px rgba(255,215,0,0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(0,0,0,0.4)';
                        e.target.style.transform = 'translateY(0) scale(1)';
                        e.target.style.boxShadow = '0 10px 25px rgba(0,0,0,0.4), 0 0 20px rgba(255,215,0,0.2)';
                      }}
                    >
                      <div className="position-relative d-flex flex-column align-items-center gap-1">
                        <div style={{ 
                          fontSize: '1rem', 
                          color: '#ffffff',
                          letterSpacing: '0.3px',
                          textShadow: '0 2px 6px rgba(0,0,0,0.8)'
                        }}>
                          PLAN BÁSICO
                        </div>
                        
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ 
                            fontSize: '1.2rem',
                            color: '#cccccc',
                            textDecoration: 'line-through',
                            textShadow: '0 2px 4px rgba(0,0,0,0.6)'
                          }}>S/ 50</span>
                        </div>
                        
                        <div className="d-flex align-items-center gap-1">
                          <span style={{ 
                            fontSize: '1.1rem', 
                            color: '#ffd700',
                            textShadow: '0 2px 6px rgba(0,0,0,0.8)'
                          }}>S/</span>
                          <span style={{ 
                            fontSize: '2.5rem',
                            fontWeight: '900',
                            textShadow: '0 4px 12px rgba(0,0,0,0.8)',
                            lineHeight: '1',
                            color: '#ffd700'
                          }}>25</span>
                        </div>
                        
                        <div style={{ 
                          fontSize: '0.8rem', 
                          color: '#e8e8e8',
                          textShadow: '0 2px 4px rgba(0,0,0,0.6)'
                        }}>
                          por mes
                        </div>
                        
                        <div style={{
                          fontSize: '1rem',
                          color: '#ffffff',
                          background: 'rgba(25,135,84,0.6)',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          border: '2px solid rgba(25,135,84,0.8)',
                          textShadow: '0 1px 3px rgba(0,0,0,0.7)'
                        }}>
                          ¡Ahorra S/ 25!
                        </div>
                        
                        <ArrowRight size={18} className="mt-1" style={{ 
                          color: '#ffd700',
                          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))'
                        }} />
                      </div>
                    </button>
                  </div>
                </div>
                
                <div className="col-md-6 col-12">
                  <div className="position-relative">
                    {/* Badge más popular */}
                    <div className="position-absolute top-0 start-50 translate-middle z-3">
                      <div className="d-flex align-items-center gap-1 px-3 py-1 fw-bold" style={{
                        background: 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)',
                        fontSize: '0.8rem',
                        borderRadius: '15px',
                        boxShadow: '0 10px 30px rgba(220,53,69,0.7)',
                        color: 'white',
                        textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                        letterSpacing: '0.3px'
                      }}>
                        <Star size={12} />
                        MÁS POPULAR
                        <Star size={12} />
                      </div>
                    </div>

                    {/* Etiqueta de descuento */}
                    <div className="position-absolute top-0 end-0 translate-middle-y me-2 z-3 mt-2">
                      <div className="d-flex align-items-center gap-1 px-2 py-1 fw-bold" style={{
                        background: 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)',
                        fontSize: '0.75rem',
                        borderRadius: '15px',
                        boxShadow: '0 6px 20px rgba(220,53,69,0.6)',
                        color: 'white',
                        textShadow: '0 2px 4px rgba(0,0,0,0.7)'
                      }}>
                        64% OFF
                      </div>
                    </div>
                    
                    <button 
                      className="btn w-100 py-3 fw-bold position-relative overflow-hidden mt-2"
                      onClick={() => handlePlanClick('plus')}
                      style={{
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #0f3460 100%)',
                        border: '3px solid rgba(255,215,0,0.8)',
                        color: 'white',
                        borderRadius: '16px',
                        boxShadow: '0 12px 35px rgba(26,26,46,0.6), 0 0 25px rgba(255,215,0,0.3)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        minHeight: '128px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-3px) scale(1.02)';
                        e.target.style.boxShadow = '0 18px 45px rgba(26,26,46,0.7), 0 0 35px rgba(255,215,0,0.4)';
                        e.target.style.borderColor = 'rgba(255,215,0,1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0) scale(1)';
                        e.target.style.boxShadow = '0 12px 35px rgba(26,26,46,0.6), 0 0 25px rgba(255,215,0,0.3)';
                        e.target.style.borderColor = 'rgba(255,215,0,0.8)';
                      }}
                    >
                      <div className="position-absolute top-0 start-0 w-100 h-100 rounded-4" style={{
                        background: 'linear-gradient(45deg, transparent 40%, rgba(255,215,0,0.15) 50%, transparent 60%)',
                        animation: 'shimmer 3s infinite'
                      }}></div>
                      
                      <div className="position-relative d-flex flex-column align-items-center gap-1">
                        <div style={{ 
                          fontSize: '1rem', 
                          color: '#ffffff',
                          letterSpacing: '0.3px',
                          textShadow: '0 2px 6px rgba(0,0,0,0.8)',
                          fontWeight: '700'
                        }}>
                          PLAN PLUS
                        </div>
                        
                        <div className="d-flex align-items-center gap-2">
                          <span style={{ 
                            fontSize: '1.2rem',
                            color: '#cccccc',
                            textDecoration: 'line-through',
                            textShadow: '0 2px 4px rgba(0,0,0,0.6)'
                          }}>S/ 180</span>
                        </div>
                        
                        <div className="d-flex align-items-center gap-1">
                          <span style={{ 
                            fontSize: '1.1rem', 
                            color: '#ffd700',
                            textShadow: '0 2px 6px rgba(0,0,0,0.8)'
                          }}>S/</span>
                          <span style={{ 
                            fontSize: '2.7rem',
                            fontWeight: '900',
                            textShadow: '0 5px 15px rgba(0,0,0,0.8)',
                            lineHeight: '1',
                            color: '#ffd700'
                          }}>45</span>
                        </div>
                        
                        <div style={{ 
                          fontSize: '0.8rem', 
                          color: '#e8e8e8',
                          textShadow: '0 2px 4px rgba(0,0,0,0.6)'
                        }}>
                          por mes
                        </div>

                        <div style={{
                          fontSize: '1rem',
                          color: '#ffffff',
                          background: 'rgba(220,53,69,0.6)',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          border: '2px solid rgba(220,53,69,0.8)',
                          textShadow: '0 1px 3px rgba(0,0,0,0.7)'
                        }}>
                          ¡Ahorra S/ 135!
                        </div>
                        
                        <ArrowRight size={18} className="mt-1" style={{ 
                          color: '#ffd700',
                          filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.8))'
                        }} />
                      </div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Mensaje final más compacto */}
              <div className="text-center">
                <div className="d-inline-flex align-items-center gap-2 px-4 py-2 rounded-pill" style={{
                  background: 'rgba(0,0,0,0.5)',
                  backdropFilter: 'blur(15px)',
                  border: '2px solid rgba(255,215,0,0.5)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.4), 0 0 20px rgba(255,215,0,0.2)'
                }}>
                  <Gift size={14} className="text-warning" />
                  <span className="fw-bold" style={{ 
                    fontSize: '0.95rem',
                    color: '#ffffff',
                    textShadow: '0 2px 6px rgba(0,0,0,0.8)'
                  }}>
                    Promoción exclusiva para usuarios de prueba gratuita
                  </span>
                  <Sparkles size={14} className="text-warning" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Detalles del Plan - MUY COMPACTO */}
      {showPlanDetails && selectedPlan && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(15px)',
            zIndex: 10001,
            animation: 'fadeIn 0.3s ease'
          }}
          onClick={() => setShowPlanDetails(false)}
        >
          <div 
            className="position-relative mx-3"
            style={{
              maxWidth: '600px',
              width: '95%',
              maxHeight: '85vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="position-relative overflow-hidden" style={{
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
              borderRadius: '24px',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,215,0,0.2)',
              border: '2px solid rgba(255,215,0,0.4)'
            }}>
              
              {/* Botón cerrar */}
              <button
                onClick={() => setShowPlanDetails(false)}
                className="position-absolute top-0 end-0 m-3 d-flex align-items-center justify-content-center"
                style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(71, 85, 105, 0.1)',
                  border: '2px solid rgba(71, 85, 105, 0.2)',
                  borderRadius: '50%',
                  color: '#64748b',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  zIndex: 10
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(71, 85, 105, 0.2)';
                  e.target.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(71, 85, 105, 0.1)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <X size={20} />
              </button>

              {/* Contenido del modal - MUY COMPACTO */}
              <div className="px-4 py-4">
                <div className="text-center mb-3">
                  <div style={{ fontSize: '3rem', marginBottom: '10px' }}>
                    {planDetails[selectedPlan].icon}
                  </div>
                  <h2 style={{ 
                    color: '#1e293b', 
                    fontWeight: '800',
                    marginBottom: '8px',
                    fontSize: '2rem'
                  }}>
                    {planDetails[selectedPlan].name}
                  </h2>
                  <p style={{ 
                    color: '#64748b', 
                    fontSize: '1rem',
                    marginBottom: '20px',
                    lineHeight: '1.5',
                    maxWidth: '500px',
                    margin: '0 auto 20px auto'
                  }}>
                    {planDetails[selectedPlan].description}
                  </p>
                  
                  {/* Precios más compactos */}
                  <div className="d-flex justify-content-center align-items-center gap-3 mb-3">
                    <span style={{
                      fontSize: '1.2rem',
                      color: '#94a3b8',
                      textDecoration: 'line-through',
                      fontWeight: '600'
                    }}>
                      S/ {planDetails[selectedPlan].originalPrice}
                    </span>
                    <span style={{
                      fontSize: '3rem',
                      fontWeight: '900',
                      color: selectedPlan === 'basico' ? '#059669' : '#d97706',
                      textShadow: '0 4px 8px rgba(0,0,0,0.1)'
                    }}>
                      S/ {planDetails[selectedPlan].price}
                    </span>
                    <span style={{ 
                      color: '#64748b', 
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}>
                      /mes
                    </span>
                  </div>
                  
                  <div style={{
                    background: selectedPlan === 'basico' 
                      ? 'linear-gradient(135deg, rgba(5,150,105,0.1) 0%, rgba(16,185,129,0.1) 100%)' 
                      : 'linear-gradient(135deg, rgba(217,119,6,0.1) 0%, rgba(245,158,11,0.1) 100%)',
                    padding: '12px 20px',
                    borderRadius: '20px',
                    display: 'inline-block',
                    marginBottom: '25px',
                    border: selectedPlan === 'basico' 
                      ? '2px solid rgba(5,150,105,0.2)' 
                      : '2px solid rgba(217,119,6,0.2)'
                  }}>
                    <span style={{
                      color: selectedPlan === 'basico' ? '#059669' : '#d97706',
                      fontWeight: '800',
                      fontSize: '0.95rem',
                      letterSpacing: '0.3px'
                    }}>
                      🎉 PRECIO PROMOCIONAL - ¡AHORRA S/ {parseInt(planDetails[selectedPlan].originalPrice) - parseInt(planDetails[selectedPlan].price)}!
                    </span>
                  </div>
                </div>

                {/* Características más compactas */}
                <div className="mb-4">
                  <h4 style={{ 
                    color: '#1e293b', 
                    fontWeight: '800',
                    marginBottom: '20px',
                    textAlign: 'center',
                    fontSize: '1.4rem'
                  }}>
                    ¿Qué incluye este plan?
                  </h4>
                  <div style={{ maxWidth: '550px', margin: '0 auto' }}>
                    {planDetails[selectedPlan].features.map((feature, index) => (
                      <div key={index} className="d-flex align-items-start gap-3 mb-3" style={{
                        padding: '12px 16px',
                        background: 'linear-gradient(135deg, rgba(5,150,105,0.05) 0%, rgba(16,185,129,0.05) 100%)',
                        borderRadius: '12px',
                        border: '2px solid rgba(5,150,105,0.1)',
                        transition: 'all 0.3s ease',
                        cursor: 'default'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(5,150,105,0.1) 0%, rgba(16,185,129,0.1) 100%)';
                        e.currentTarget.style.transform = 'translateX(6px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(5,150,105,0.05) 0%, rgba(16,185,129,0.05) 100%)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}>
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: '1px'
                        }}>
                          <span style={{ 
                            color: 'white', 
                            fontSize: '0.8rem',
                            fontWeight: '800'
                          }}>✓</span>
                        </div>
                        <span style={{ 
                          color: '#334155', 
                          fontSize: '0.95rem',
                          lineHeight: '1.5',
                          fontWeight: '500'
                        }}>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Botón de WhatsApp más compacto */}
                <div className="text-center">
                  <button
                    onClick={() => handleWhatsAppContact(planDetails[selectedPlan].name, planDetails[selectedPlan].price)}
                    style={{
                      background: 'linear-gradient(135deg, #25d366 0%, #20b15a 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '25px',
                      padding: '16px 40px',
                      fontSize: '1.1rem',
                      fontWeight: '800',
                      boxShadow: '0 10px 30px rgba(37,211,102,0.4)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      margin: '0 auto',
                      letterSpacing: '0.3px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px) scale(1.03)';
                      e.target.style.boxShadow = '0 15px 40px rgba(37,211,102,0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0) scale(1)';
                      e.target.style.boxShadow = '0 10px 30px rgba(37,211,102,0.4)';
                    }}
                  >
                    <span style={{ fontSize: '1.3rem' }}>📱</span>
                    <span>Adquirir por WhatsApp</span>
                  </button>
                  
                  <p style={{ 
                    color: '#64748b', 
                    fontSize: '0.9rem',
                    marginTop: '15px',
                    marginBottom: '0',
                    fontWeight: '500'
                  }}>
                    Te contactaremos inmediatamente para procesar tu suscripción
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.7; }
          33% { transform: translateY(-12px) rotate(8deg); opacity: 0.9; }
          66% { transform: translateY(6px) rotate(-5deg); opacity: 0.5; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%) rotate(45deg); }
          100% { transform: translateX(200%) rotate(45deg); }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
        }
        
        @keyframes blink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.1); }
        }
        
        @keyframes bounce {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-3px) scale(1.05); }
        }
        
        @keyframes backgroundShift {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.85; }
        }

        @keyframes fadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        
        .z-3 { z-index: 3; }
        .transition-all { transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
        .scale-100 { transform: scale(1); }
        .scale-95 { transform: scale(0.95); }
        .translate-y-0 { transform: translateY(0); }
        .translate-y-4 { transform: translateY(1rem); }
        .opacity-100 { opacity: 1; }
        .opacity-0 { opacity: 0; }

        @media (max-width: 768px) {
          .col-md-6 {
            margin-bottom: 0.75rem;
          }
        }

        /* Scrollbar personalizado para modal */
        div::-webkit-scrollbar {
          width: 6px;
        }
        
        div::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.1);
          border-radius: 10px;
        }
        
        div::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.3);
          border-radius: 10px;
        }
        
        div::-webkit-scrollbar-thumb:hover {
          background: rgba(0,0,0,0.5);
        }
      `}</style>
    </>
  );
};

export default PromoTimer;